// ui/replay.ts — Cluster 18: replay scrubber UI (controls + render loop)
// Moved from main.ts. drawerView is injected via setReplayGetDrawerViewCb()
// so this module does not import main.ts.

import { $ } from './dom';
import { fmtReplayClock } from './format';
import { REPLAY_STEPS, REPLAY_SWEEP_MS, replay } from '../state/replay';
import { replaySourcePings } from '../map/elevation';
import { getLeafletMap } from '../state/app-state';
import { drawRoutes } from '../map/tracker';
import { renderShareViewerTrail } from './share-viewer';
import { cmdConsole } from '../state/auth-state';
import { renderCommandFeed } from './command-console';

// ── callback injected by main.ts boot() ──────────────────────────────────
// drawerView is a main.ts let; injected so this module does not import main.
let _replayGetDrawerViewCb: (() => string) | null = null;
export function setReplayGetDrawerViewCb(fn: () => string): void { _replayGetDrawerViewCb = fn; }

// replaySourcePings, replayClip, updateElevationPanel moved to ./map/elevation
function replayRerender(){
  if(document.body.dataset.mode === 'share-viewer') renderShareViewerTrail();
  else if(getLeafletMap()) drawRoutes();
  // Keep the command-console feed in lock-step with the scrubber (web spec
  // commandsAtCutoff) when the Device sub-view is open for this device.
  const _dv = _replayGetDrawerViewCb ? _replayGetDrawerViewCb() : '';
  if(_dv === 'device' && cmdConsole.token) renderCommandFeed();
}

// ── Elevation panel (ADR 0013) ──────────────────────────────────────────
// ensureElevationPanel, elevSparkline, updateElevationPanel,
// replaySourcePings, replayClip moved to ./map/elevation
export function replayAtEdge(): boolean {
  return replay.cutoffT === Infinity || replay.cutoffT >= replay.maxT;
}
export function replayStopLoop(){
  replay.playing = false;
  if(replay.rafId){ cancelAnimationFrame(replay.rafId); replay.rafId = null; }
}

export function replayHide(){
  const bar = $('replay-bar'); if(bar) (bar as any).hidden = true;
  replayStopLoop();
  replay.ready = false;
  replay.cutoffT = Infinity;   // so the next full render shows everything
}

// Recompute range + visibility. Called from the render chokepoints — must
// never re-render the map itself (would recurse).
export function syncReplay(){
  const bar = $('replay-bar'); if(!bar) return;
  const onMap = document.body.dataset.mode === 'share-viewer'
    || $('screen-map')?.classList.contains('is-active');
  const pings = onMap ? replaySourcePings() : null;
  if(!pings || pings.length < 2 || pings[pings.length-1].t <= pings[0].t){
    replayHide();
    return;
  }
  const wasAtEdge = !replay.ready || (!replay.playing && replayAtEdge());
  replay.ready = true;
  replay.minT = pings[0].t;
  replay.maxT = pings[pings.length-1].t;
  // Follow the live edge only if the user is parked there; otherwise hold the
  // scrub position (and let the now-wider range reposition the thumb).
  if(wasAtEdge && !replay.playing) replay.cutoffT = Infinity;
  (bar as any).hidden = false;
  replayUpdateUi();
}

export function replayUpdateUi(){
  const slider = $('replay-slider') as HTMLInputElement | null;
  const timeEl = $('replay-time');
  const liveBtn = $('replay-live');
  const playIcon = $('replay-play-icon');
  const span = replay.maxT - replay.minT;
  const atEdge = replayAtEdge();
  const frac = atEdge || span <= 0 ? 1 : (replay.cutoffT - replay.minT) / span;
  if(slider) slider.value = String(Math.round(frac * REPLAY_STEPS));
  if(timeEl) timeEl.textContent = atEdge ? `live · ${fmtReplayClock(replay.maxT)}` : fmtReplayClock(replay.cutoffT);
  if(liveBtn) liveBtn.classList.toggle('is-live', atEdge);
  if(playIcon){
    playIcon.classList.toggle('ph-play', !replay.playing);
    playIcon.classList.toggle('ph-pause', replay.playing);
  }
}

export function replayPause(){ replayStopLoop(); replayUpdateUi(); }

export function replayPlay(){
  if(replay.maxT <= replay.minT) return;
  // Starting from the live edge replays from the beginning.
  if(replayAtEdge()) replay.cutoffT = replay.minT;
  replay.playing = true;
  replay.lastFrame = performance.now();
  replayRerender();
  replayUpdateUi();
  replay.rafId = requestAnimationFrame(replayFrame);
}

export function replayFrame(now: number){
  if(!replay.playing) return;
  const dt = now - replay.lastFrame;
  replay.lastFrame = now;
  const span = replay.maxT - replay.minT;
  const base = replay.cutoffT === Infinity ? replay.maxT : replay.cutoffT;
  const next = base + (dt / REPLAY_SWEEP_MS) * span;
  if(next >= replay.maxT){
    replay.cutoffT = Infinity;   // reached the end → snap to live edge + stop
    replayRerender();
    replayPause();
    return;
  }
  replay.cutoffT = next;
  replayRerender();
  replayUpdateUi();
  replay.rafId = requestAnimationFrame(replayFrame);
}

export function initReplayControls(){
  const slider = $('replay-slider') as HTMLInputElement | null;
  const playBtn = $('replay-play');
  const liveBtn = $('replay-live');
  if(slider) slider.addEventListener('input', ()=>{
    replayStopLoop();   // scrubbing pauses playback
    const frac = (+slider.value) / REPLAY_STEPS;
    replay.cutoffT = frac >= 1 ? Infinity : replay.minT + frac * (replay.maxT - replay.minT);
    replayRerender();
    replayUpdateUi();
  });
  if(playBtn) playBtn.addEventListener('click', ()=> replay.playing ? replayPause() : replayPlay());
  if(liveBtn) liveBtn.addEventListener('click', ()=>{
    replayStopLoop();
    replay.cutoffT = Infinity;
    replayRerender();
    replayUpdateUi();
  });
}
